import styled, { css } from 'styled-components';

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'outline' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

interface StyledButtonProps {
  $variant: ButtonVariant;
  $size: ButtonSize;
  $iconOnly: boolean;
  $fullWidth: boolean;
  $loading: boolean;
}

const sizeStyles: Record<ButtonSize, ReturnType<typeof css>> = {
  sm: css`
    height: 32px;
    padding: 0 ${({ theme }) => theme.spacing[3]};
    font-size: ${({ theme }) => theme.fontSize.sm};
  `,
  md: css`
    height: 40px;
    padding: 0 ${({ theme }) => theme.spacing[6]};
    font-size: ${({ theme }) => theme.fontSize.base};
  `,
  lg: css`
    height: 48px;
    padding: 0 ${({ theme }) => theme.spacing[8]};
    font-size: ${({ theme }) => theme.fontSize.base};
  `,
};

const iconOnlySizeStyles: Record<ButtonSize, ReturnType<typeof css>> = {
  sm: css`
    width: 32px;
    height: 32px;
    padding: 8px;
  `,
  md: css`
    width: 40px;
    height: 40px;
    padding: 10px;
  `,
  lg: css`
    width: 48px;
    height: 48px;
    padding: 12px;
  `,
};

const variantStyles: Record<ButtonVariant, ReturnType<typeof css>> = {
  primary: css`
    background-color: ${({ theme }) => theme.colors.primary[900]};
    color: #ffffff;
    border: none;

    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.colors.primary[700]};
      color: #ffffff;
    }
  `,
  secondary: css`
    background-color: ${({ theme }) =>
      theme.mode === 'dark'
        ? theme.colors.surfaceRaised
        : theme.colors.neutral[200]};
    color: ${({ theme }) => theme.colors.text.secondary};
    border: none;

    &:hover:not(:disabled) {
      background-color: ${({ theme }) =>
        theme.mode === 'dark'
          ? theme.colors.surfaceHover
          : theme.colors.neutral[300]};
      color: ${({ theme }) => theme.colors.text.secondary};
    }
  `,
  danger: css`
    background-color: ${({ theme }) => theme.colors.error.base};
    color: #ffffff;
    border: none;

    &:hover:not(:disabled) {
      background-color: ${({ theme }) =>
        theme.colors.error.hover || theme.colors.error.dark};
      color: #ffffff;
    }
  `,
  outline: css`
    background-color: transparent;
    color: ${({ theme }) => theme.colors.primary.base};
    border: 1px solid ${({ theme }) => theme.colors.primary.base};

    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.colors.primary.base};
      color: #ffffff;
    }
  `,
  ghost: css`
    background-color: transparent;
    color: ${({ theme }) => theme.colors.text.secondary};
    border: none;

    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.colors.surfaceHover};
    }
  `,
};

const sharedButtonStyles = css<StyledButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[2]};
  cursor: pointer;
  white-space: nowrap;
  font-family: ${({ theme }) => theme.fontFamily};
  font-weight: ${({ theme }) => theme.fontWeight.semiBold};
  text-transform: uppercase;
  text-decoration: none;
  border-radius: ${({ theme, $iconOnly }) =>
    $iconOnly ? theme.borderRadius.md : theme.borderRadius.base};
  line-height: normal;
  transition:
    background-color ${({ theme }) => theme.transition.base},
    color ${({ theme }) => theme.transition.fast},
    transform ${({ theme }) => theme.transition.fast},
    box-shadow ${({ theme }) => theme.transition.base},
    border-color ${({ theme }) => theme.transition.base},
    opacity ${({ theme }) => theme.transition.fast};
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};

  /* Size */
  ${({ $iconOnly, $size }: StyledButtonProps) =>
    $iconOnly ? iconOnlySizeStyles[$size] : sizeStyles[$size]}

  /* Variant */
  ${({ $variant }: StyledButtonProps) => variantStyles[$variant]}

  /* Hover - shared transform */
  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  /* Active */
  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  /* Focus visible */
  &:focus-visible {
    box-shadow: ${({ theme }) => theme.focus.ring};
  }

  /* Loading */
  ${({ $loading }) =>
    $loading &&
    css`
      pointer-events: none;
      opacity: 0.8;
    `}

  /* Spin icon alignment inside button */
  .ant-spin {
    display: inline-flex;
    align-items: center;
    line-height: 0;
  }
`;

export const StyledButton = styled.button<StyledButtonProps>`
  ${sharedButtonStyles}

  /* Disabled */
  &:disabled {
    background-color: ${({ theme }) =>
      theme.mode === 'dark'
        ? theme.colors.surfaceRaised
        : theme.colors.neutral[300]};
    color: ${({ theme }) => theme.colors.text.muted};
    border-color: transparent;
    cursor: not-allowed;

    &:hover {
      transform: none;
    }
  }
`;

export const StyledButtonLink = styled.a<StyledButtonProps>`
  ${sharedButtonStyles}
`;
